/**
 * Temporary debug component to examine volume data
 * Add <VolumeDebug symbol="LCII" /> to any page to see the data
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  TextField,
  Button,
} from '@mui/material';
import { collection, query, where, orderBy, getDocs, limit } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { format } from 'date-fns';

interface SnapshotRow {
  id: string;
  date: Date;
  userId: string | null;
  volume: number | null;
  volumeChgVs50Day: number | null;
  avgDailyVolume: number | null;
  currentPrice: number | null;
  source: string;
}

interface VolumeDebugProps {
  symbol?: string;
}

export const VolumeDebug: React.FC<VolumeDebugProps> = ({ symbol: initialSymbol = 'LCII' }) => {
  const [symbol, setSymbol] = useState(initialSymbol);
  const [inputSymbol, setInputSymbol] = useState(initialSymbol);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<SnapshotRow[]>([]);
  const [stats, setStats] = useState<{
    total: number;
    withVolume: number;
    withoutVolume: number;
    uniqueUserIds: string[];
    uniqueDates: number;
    userSpecific: number;
    noUserId: number;
  } | null>(null);

  const currentUserId = auth.currentUser?.uid;

  const loadData = async () => {
    setLoading(true);

    try {
      // Query ALL snapshots for this symbol (no userId filter)
      const allSnapshotsQuery = query(
        collection(db, 'watchlistSnapshots'),
        where('symbol', '==', symbol),
        orderBy('timestamp', 'desc'),
        limit(100)
      );

      const allSnapshots = await getDocs(allSnapshotsQuery);

      const rows: SnapshotRow[] = [];
      allSnapshots.forEach((doc) => {
        const d = doc.data();
        const timestamp = d.timestamp?.toDate ? d.timestamp.toDate() : new Date(d.timestamp);
        rows.push({
          id: doc.id,
          date: timestamp,
          userId: d.userId || null,
          volume: d.volume ?? null,
          volumeChgVs50Day: d.volumeChgVs50Day ?? null,
          avgDailyVolume: d.avgDailyVolume ?? null,
          currentPrice: d.currentPrice ?? null,
          source: d.source || 'unknown',
        });
      });

      // Sort by date descending
      rows.sort((a, b) => b.date.getTime() - a.date.getTime());

      // Calculate stats
      const uniqueUserIds = Array.from(new Set(rows.map(r => r.userId).filter(Boolean))) as string[];
      const uniqueDates = new Set(rows.map(r => format(r.date, 'yyyy-MM-dd'))).size;
      const withVolume = rows.filter(r => r.volume !== null).length;
      const withoutVolume = rows.filter(r => r.volume === null).length;
      const userSpecific = rows.filter(r => r.userId !== null).length;
      const noUserId = rows.filter(r => r.userId === null).length;

      setData(rows);
      setStats({
        total: rows.length,
        withVolume,
        withoutVolume,
        uniqueUserIds,
        uniqueDates,
        userSpecific,
        noUserId,
      });
    } catch (error) {
      console.error('Error loading volume data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [symbol]);

  const handleSearch = () => {
    setSymbol(inputSymbol.toUpperCase());
  };

  const formatVolume = (vol: number | null) => {
    if (vol === null) return '-';
    if (vol >= 1000000) return `${(vol / 1000000).toFixed(2)}M`;
    if (vol >= 1000) return `${(vol / 1000).toFixed(1)}K`;
    return vol.toString();
  };

  return (
    <Paper sx={{ p: 3, m: 2 }}>
      <Typography variant="h5" gutterBottom>
        Volume Data Debug - {symbol}
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
        <TextField
          size="small"
          label="Symbol"
          value={inputSymbol}
          onChange={(e) => setInputSymbol(e.target.value.toUpperCase())}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
        />
        <Button variant="contained" onClick={handleSearch}>
          Load
        </Button>
        <Typography variant="body2" color="text.secondary">
          Current User ID: {currentUserId?.substring(0, 12)}...
        </Typography>
      </Box>

      {loading && <CircularProgress />}

      {stats && (
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Chip label={`Total: ${stats.total}`} color="primary" />
          <Chip label={`With Volume: ${stats.withVolume}`} color="success" />
          <Chip label={`Without Volume: ${stats.withoutVolume}`} color="warning" />
          <Chip label={`User-Specific: ${stats.userSpecific}`} color="info" />
          <Chip label={`No UserId (Alpaca): ${stats.noUserId}`} color="secondary" />
          <Chip label={`Unique Dates: ${stats.uniqueDates}`} />
        </Box>
      )}

      {stats && stats.uniqueUserIds.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          UserIds found: {stats.uniqueUserIds.map(id => id.substring(0, 12) + '...').join(', ')}
        </Typography>
      )}

      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date/Time</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>UserId</TableCell>
              <TableCell align="right">Volume</TableCell>
              <TableCell align="right">Vol Chg %</TableCell>
              <TableCell align="right">Avg Daily Vol</TableCell>
              <TableCell align="right">Price</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row) => (
              <TableRow
                key={row.id}
                sx={{
                  bgcolor: row.userId === currentUserId ? 'action.selected' :
                           row.userId === null ? 'warning.light' : 'inherit',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <TableCell>{format(row.date, 'yyyy-MM-dd HH:mm')}</TableCell>
                <TableCell>
                  <Chip
                    label={row.source}
                    size="small"
                    color={row.source === 'alpaca' ? 'secondary' : row.source === 'marketsurge' ? 'primary' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  {row.userId ? (
                    row.userId === currentUserId ? (
                      <Chip label="You" size="small" color="success" />
                    ) : (
                      row.userId.substring(0, 8) + '...'
                    )
                  ) : (
                    <Chip label="None" size="small" color="warning" />
                  )}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: row.volume ? 'bold' : 'normal' }}>
                  {formatVolume(row.volume)}
                </TableCell>
                <TableCell align="right">
                  {row.volumeChgVs50Day !== null ? `${row.volumeChgVs50Day.toFixed(1)}%` : '-'}
                </TableCell>
                <TableCell align="right">
                  {formatVolume(row.avgDailyVolume)}
                </TableCell>
                <TableCell align="right">
                  {row.currentPrice !== null ? `$${row.currentPrice.toFixed(2)}` : '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          Data Sources:
        </Typography>
        <Typography variant="body2" sx={{ fontSize: 12 }}>
          <strong>Alpaca:</strong> Real-time price/volume data (no userId, preferred for charts)<br />
          <strong>MarketSurge:</strong> User imports with ratings data (has userId, excluded from charts)<br />
          Charts query ALL snapshots and prefer Alpaca data for accurate price/volume history.
        </Typography>
      </Box>
    </Paper>
  );
};

export default VolumeDebug;
